import { Link, useParams } from 'react-router';
import { Skeleton } from '../../components/Skeleton';
import { useQuery } from '@tanstack/react-query';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Section } from '../../components/Section';
import { DepartmentOrderEditor } from '../../components/stores/DepartmentOrderEditor';
import { FavouriteButton } from '../../components/stores/FavouriteButton';
import { ProductShelfLifeSection } from '../../components/stores/ProductShelfLifeSection';
import { ShelfLifeSection } from '../../components/stores/ShelfLifeSection';
import { StoreRadiusSection } from '../../components/stores/StoreRadiusSection';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { storeQueryKeys } from './queryKeys';

export function StoreDetailPage() {
  const { id = '' } = useParams<{ id: string }>();

  const store = useQuery({
    queryKey: storeQueryKeys.detail(id),
    queryFn: () => api.stores.get(id),
    enabled: id !== '',
  });

  return (
    <AppShell title={store.data?.name ?? 'Store'}>
      <div className="flex flex-col gap-4">
        <Link to="/stores" className="text-sm text-muted underline-offset-2 hover:underline">
          ← All stores
        </Link>

        {store.isPending ? <Skeleton lines={3} /> : null}
        {store.isError ? <Alert tone="error">{messageForError(store.error)}</Alert> : null}

        {store.data ? (
          <>
            <Section title={store.data.name}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-muted">
                    {store.data.chain}
                    {store.data.address ? ` · ${store.data.address}` : ''}
                  </p>
                </div>
                <FavouriteButton storeId={store.data.id} favourite={store.data.favourite} />
              </div>
            </Section>

            <Section
              title="Detection radius"
              description="How close the phone needs to be to suggest this store. Only the household that added the store can change it."
            >
              <StoreRadiusSection key={store.data.id} store={store.data} />
            </Section>

            <Section
              title="Walking order"
              description="The order departments come up as you shop — used to sort grocery lists."
            >
              <DepartmentOrderEditor storeId={store.data.id} order={store.data.departmentOrder} />
            </Section>

            <Section
              title="Shelf-life rules"
              description="Adjust how long items keep after a trip to this store (D6). Category rules override the whole-store rule; product overrides win over both."
            >
              <ShelfLifeSection storeId={store.data.id} />
            </Section>

            <Section
              title="Product overrides"
              description="The most specific shelf-life rule — set per product at this store."
            >
              <ProductShelfLifeSection storeId={store.data.id} />
            </Section>
          </>
        ) : null}
      </div>
    </AppShell>
  );
}
